import { useRef, useEffect } from 'react';

// ==================== CHART TYPES ====================
export interface ChartData {
  type: 'bar' | 'pie' | 'line' | 'horizontal_bar';
  title?: string;
  labels: string[];
  values: number[];
  prefix?: string;
  suffix?: string;
  colors?: string[];
}

const CHART_COLORS = [
  '#8b5cf6', '#6366f1', '#3b82f6', '#06b6d4', '#10b981',
  '#f59e0b', '#ef4444', '#ec4899', '#84cc16', '#f97316',
  '#14b8a6', '#a855f7', '#e11d48', '#0ea5e9', '#22c55e',
];

function val2str(v: number): string {
  if (v >= 1000) return v.toLocaleString('pt-BR', { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 });
  return v % 1 ? v.toFixed(2).replace('.', ',') : String(v);
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  if (h <= 0) return;
  r = Math.min(r, h / 2, w / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fill();
}

export function extractCharts(text: string): ChartData[] {
  const charts: ChartData[] = [];
  const regex = /```chart\s*\n?([\s\S]*?)```/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    try {
      const json = match[1].trim();
      const parsed = JSON.parse(json);
      if (parsed.labels && parsed.values && parsed.type) {
        charts.push(parsed as ChartData);
      }
    } catch (e) { /* ignore parse errors */ }
  }
  return charts;
}

export function stripChartBlocks(text: string): string {
  return text.replace(/```chart\s*\n?[\s\S]*?```/g, '').trim();
}

export default function AIChart({ data, compact = false }: { data: ChartData; compact?: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const dpr = window.devicePixelRatio || 1;
    const w = compact ? 260 : 380;
    const h = compact ? 190 : 260;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);
    
    const colors = data.colors || CHART_COLORS;
    const prefix = data.prefix || '';
    const suffix = data.suffix || '';
    const maxVal = Math.max(...data.values, 1);
    
    // Title
    if (data.title) {
      ctx.fillStyle = '#374151';
      ctx.font = `bold ${compact ? 11 : 13}px system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(data.title, w / 2, compact ? 14 : 18);
    }
    
    const titleOffset = data.title ? (compact ? 22 : 28) : 8;
    
    if (data.type === 'pie') {
      const total = data.values.reduce((a, b) => a + b, 0);
      if (total === 0) return;
      const cx = compact ? 90 : 130;
      const cy = titleOffset + (compact ? 70 : 95);
      const r = compact ? 55 : 80;
      let startAngle = -Math.PI / 2;
      
      data.values.forEach((val, i) => {
        const sliceAngle = (val / total) * 2 * Math.PI;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, r, startAngle, startAngle + sliceAngle);
        ctx.closePath();
        ctx.fillStyle = colors[i % colors.length];
        ctx.fill();
        
        if (sliceAngle > 0.3) {
          const midAngle = startAngle + sliceAngle / 2;
          const lx = cx + (r * 0.6) * Math.cos(midAngle);
          const ly = cy + (r * 0.6) * Math.sin(midAngle);
          ctx.fillStyle = '#fff';
          ctx.font = `bold ${compact ? 9 : 10}px system-ui`;
          ctx.textAlign = 'center';
          ctx.fillText(`${((val / total) * 100).toFixed(0)}%`, lx, ly + 3);
        }
        startAngle += sliceAngle;
      });
      
      // Legend
      const legendX = compact ? 160 : 230;
      const legendY = titleOffset + (compact ? 10 : 20);
      const lineH = compact ? 15 : 19;
      data.labels.forEach((label, i) => {
        const y = legendY + i * lineH;
        if (y > h - 10) return;
        ctx.fillStyle = colors[i % colors.length];
        ctx.fillRect(legendX, y - 5, compact ? 8 : 10, compact ? 8 : 10);
        ctx.fillStyle = '#4b5563';
        ctx.font = `${compact ? 8 : 10}px system-ui`;
        ctx.textAlign = 'left';
        const maxLbl = compact ? 10 : 16;
        const lblText = label.length > maxLbl ? label.substring(0, maxLbl - 1) + '…' : label;
        ctx.fillText(`${lblText}: ${prefix}${val2str(data.values[i])}${suffix}`, legendX + (compact ? 11 : 14), y + 3);
      });
      
    } else if (data.type === 'horizontal_bar') {
      const barAreaTop = titleOffset + 5;
      const barH = Math.min(compact ? 16 : 22, (h - barAreaTop - 10) / data.labels.length - 4);
      const labelW = compact ? 70 : 110;
      const barAreaW = w - labelW - (compact ? 45 : 60);
      
      data.labels.forEach((label, i) => {
        const y = barAreaTop + i * (barH + (compact ? 5 : 8));
        ctx.fillStyle = '#4b5563';
        ctx.font = `${compact ? 8 : 10}px system-ui`;
        ctx.textAlign = 'right';
        const maxLbl = compact ? 10 : 16;
        const lblText = label.length > maxLbl ? label.substring(0, maxLbl - 1) + '…' : label;
        ctx.fillText(lblText, labelW - 4, y + barH / 2 + 3);
        const barW = (data.values[i] / maxVal) * barAreaW;
        ctx.fillStyle = colors[i % colors.length];
        roundRect(ctx, labelW, y, barW, barH, 3);
        ctx.fillStyle = '#374151';
        ctx.font = `bold ${compact ? 8 : 10}px system-ui`;
        ctx.textAlign = 'left';
        ctx.fillText(`${prefix}${val2str(data.values[i])}${suffix}`, labelW + barW + 4, y + barH / 2 + 3);
      });
      
    } else if (data.type === 'line') {
      const padL = compact ? 45 : 60;
      const padR = compact ? 12 : 20;
      const padT = titleOffset + 10;
      const padB = compact ? 30 : 45;
      const chartW = w - padL - padR;
      const chartH = h - padT - padB;
      
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 0.5;
      for (let i = 0; i <= 4; i++) {
        const y = padT + (chartH / 4) * i;
        ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(w - padR, y); ctx.stroke();
        ctx.fillStyle = '#9ca3af';
        ctx.font = `${compact ? 7 : 9}px system-ui`;
        ctx.textAlign = 'right';
        const gridVal = maxVal - (maxVal / 4) * i;
        ctx.fillText(`${prefix}${val2str(gridVal)}`, padL - 4, y + 3);
      }
      
      // Area fill
      ctx.fillStyle = colors[0] + '20';
      ctx.beginPath();
      data.values.forEach((v, i) => {
        const x = padL + (i / Math.max(data.values.length - 1, 1)) * chartW;
        const y = padT + (1 - v / maxVal) * chartH;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      ctx.lineTo(padL + chartW, padT + chartH);
      ctx.lineTo(padL, padT + chartH);
      ctx.closePath();
      ctx.fill();
      
      // Line
      ctx.strokeStyle = colors[0];
      ctx.lineWidth = 2;
      ctx.beginPath();
      data.values.forEach((v, i) => {
        const x = padL + (i / Math.max(data.values.length - 1, 1)) * chartW;
        const y = padT + (1 - v / maxVal) * chartH;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      ctx.stroke();
      
      // Points
      data.values.forEach((v, i) => {
        const x = padL + (i / Math.max(data.values.length - 1, 1)) * chartW;
        const y = padT + (1 - v / maxVal) * chartH;
        ctx.fillStyle = '#fff';
        ctx.beginPath(); ctx.arc(x, y, 4, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = colors[0];
        ctx.beginPath(); ctx.arc(x, y, 3, 0, Math.PI * 2); ctx.fill();
        // X label
        ctx.fillStyle = '#6b7280';
        ctx.font = `${compact ? 7 : 9}px system-ui`;
        ctx.textAlign = 'center';
        const lbl = data.labels[i]?.length > 8 ? data.labels[i].substring(0, 7) + '…' : data.labels[i] || '';
        ctx.save(); ctx.translate(x, h - padB + 12); ctx.rotate(-0.4); ctx.fillText(lbl, 0, 0); ctx.restore();
      });
      
    } else {
      // BAR CHART (vertical - default)
      const padL = compact ? 45 : 60;
      const padR = compact ? 8 : 15;
      const padT = titleOffset + 10;
      const padB = compact ? 35 : 55;
      const chartW = w - padL - padR;
      const chartH = h - padT - padB;
      const barW = Math.min(compact ? 26 : 40, (chartW / data.labels.length) * 0.7);
      const gap = chartW / data.labels.length;
      
      // Grid
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 0.5;
      for (let i = 0; i <= 4; i++) {
        const y = padT + (chartH / 4) * i;
        ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(w - padR, y); ctx.stroke();
        ctx.fillStyle = '#9ca3af';
        ctx.font = `${compact ? 7 : 9}px system-ui`;
        ctx.textAlign = 'right';
        const gridVal = maxVal - (maxVal / 4) * i;
        ctx.fillText(`${prefix}${val2str(gridVal)}`, padL - 4, y + 3);
      }
      
      // Bars
      data.labels.forEach((label, i) => {
        const x = padL + i * gap + (gap - barW) / 2;
        const barH = (data.values[i] / maxVal) * chartH;
        const y = padT + chartH - barH;
        // Gradient effect
        ctx.fillStyle = colors[i % colors.length];
        roundRect(ctx, x, y, barW, barH, 3);
        // Value on top
        ctx.fillStyle = '#374151';
        ctx.font = `bold ${compact ? 7 : 9}px system-ui`;
        ctx.textAlign = 'center';
        ctx.fillText(`${prefix}${val2str(data.values[i])}${suffix}`, x + barW / 2, y - 4);
        // Label below
        ctx.fillStyle = '#6b7280';
        ctx.font = `${compact ? 7 : 9}px system-ui`;
        const lbl = label.length > 8 ? label.substring(0, 7) + '…' : label;
        ctx.save(); ctx.translate(x + barW / 2, padT + chartH + 10); ctx.rotate(-0.4); ctx.fillText(lbl, 0, 0); ctx.restore();
      });
    }
  }, [data, compact]);
  
  return (
    <div className={`my-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 ${compact ? 'p-1.5' : 'p-2'} inline-block`}>
      <canvas ref={canvasRef} />
    </div>
  );
}
